//base
import React, { forwardRef, HTMLInputTypeAttribute, useMemo } from 'react';

//style
import { StyledBasicTextFieldV2 } from './style';

//libraries
import {
  InputAdornment,
  TextField,
  type TextFieldVariants,
  type TextFieldProps
} from '@mui/material';

type InputType = HTMLInputTypeAttribute | 'english' | 'number' | 'engAndNum';

export interface BasicTextFieldV2Props extends Omit<TextFieldProps, 'onChange' | 'type'> {
  label?: string;
  labelPosition?: 'top' | 'left';
  type?: InputType;
  maxLength?: number;
  variant?: TextFieldVariants;
  showPercentage?: boolean;
  showDays?: boolean;
  value?: string | number | undefined;
  direction?: 'row' | 'column';
  placeholder?: string;
  width?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  noMargin?: boolean;
}

export const BasicTextFieldV2 = forwardRef<HTMLInputElement, BasicTextFieldV2Props>(
  (props, ref) => {
    const {
      type = 'text',
      maxLength,
      variant = 'outlined',
      label,
      labelPosition = 'top',
      required,
      showPercentage = false,
      showDays = false,
      onChange,
      value = undefined,
      direction = 'row',
      placeholder = '',
      className,
      noMargin = false,
      width,
      ...rest
    } = props;

    const inputValue = useMemo(() => value ?? '', [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      let newValue = value;

      if (type === 'number') {
        newValue = value.replace(/[^0-9]/g, '');
        e.target.value = newValue;
      }

      if (type === 'english') {
        newValue = value.replace(/[^a-zA-Z]/g, '');
        e.target.value = newValue;
      }

      if (type === 'engAndNum') {
        newValue = value.replace(/[^a-zA-Z0-9]/g, '');
        e.target.value = newValue;
      }

      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength);
        e.target.value = newValue;
      }

      onChange?.(e);
    };

    return (
      <StyledBasicTextFieldV2
        direction={direction}
        width={width}
        $noMargin={noMargin}
        className={`${className && className} ${labelPosition}`}
        $labelPosition={labelPosition}
      >
        {label && (
          <span className="tf-label">
            {label}
            {required && <span className="required">*</span>}
          </span>
        )}
        <TextField
          variant={variant}
          inputRef={ref}
          value={inputValue}
          type={type}
          onChange={handleChange}
          placeholder={placeholder}
          InputProps={{
            startAdornment: null,
            endAdornment: showPercentage ? (
              <InputAdornment position="end">%</InputAdornment>
            ) : showDays ? (
              'Days'
            ) : null
          }}
          {...rest}
        />
      </StyledBasicTextFieldV2>
    );
  }
);

BasicTextFieldV2.displayName = 'BasicTextFieldV2';
